import { memo, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from '@aimsos/i18n';
import { FormSelect, NumericField, SearchableSelect, Textfield } from '@aimsos/ui';

import { WIDGET_DATE_RANGE_MODES } from '../constants/widgetContract.js';
import { DEFAULT_DATE_RANGE, grainsForDateRangeMode } from '../utils/widgetTimeBinding.js';
import { humanizeKey } from '../utils/humanizeKey.js';
import BindingSlotLabel from './BindingSlotLabel.jsx';
import SettingToggle from './SettingToggle.jsx';

/**
 * `bindings.time` — the period the widget measures (fix/widgets).
 *
 * Twenty-five types declare it and nothing drew it, so no widget built here
 * could say "last 6 months". It is optional on all of them, which is why the
 * column picker starts empty: with no column the whole binding is left out
 * and the widget covers everything, exactly as it did before this existed.
 *
 * ## What the author picks
 *
 * A **date column of the source** — one of the same elements the other slots
 * draw from, because in `ENTITY` mode the dataset the backend generates
 * carries precisely those: an exposed column on the record-set path, a
 * grouper on the summary one.
 *
 * Then the range, which `$defs/date_range` makes a tagged union: each mode
 * declares its own keys and `additionalProperties: false` refuses the rest,
 * so the controls below appear per mode and `dateRange` builds that branch
 * alone. A grain left over from `LAST_N` never reaches the wire.
 */
const TimeBindingRow = memo(function TimeBindingRow({ slot, value, candidates, onChange }) {
  const { t } = useTranslation('dashboards');

  const range = value?.range ?? DEFAULT_DATE_RANGE;
  const grains = grainsForDateRangeMode(range.mode);

  const modeOptions = useMemo(
    () => WIDGET_DATE_RANGE_MODES.map((mode) => ({
      value: mode,
      label: t(`widgetBuilder.bindingSlots.time.modes.${mode}`, { defaultValue: humanizeKey(mode) }),
    })),
    [t],
  );

  const grainOptions = useMemo(
    () => grains.map((grain) => ({
      value: grain,
      label: t(`widgetBuilder.bindingSlots.time.grains.${grain}`, {
        defaultValue: humanizeKey(grain),
      }),
    })),
    [grains, t],
  );

  const patch = useCallback(
    (next) => onChange(slot.key, { ...(value ?? {}), ...next }),
    [onChange, slot.key, value],
  );

  const handleColumn = useCallback((option) => {
    if (!option?.id) {
      onChange(slot.key, null);
      return;
    }
    patch({ ref: option.id });
  }, [onChange, patch, slot.key]);

  /* A mode change rebuilds the range: its keys are not the previous one's. */
  const handleMode = useCallback((mode) => {
    const [first] = grainsForDateRangeMode(mode);
    patch({
      range: {
        mode,
        ...(first ? { grain: first } : {}),
        ...(mode === 'LAST_N' ? { n: 7 } : {}),
      },
    });
  }, [patch]);

  const handleGrain = useCallback(
    (grain) => patch({ range: { ...range, grain } }),
    [patch, range],
  );
  const handleCount = useCallback(
    (n) => patch({ range: { ...range, n: n ?? '' } }),
    [patch, range],
  );
  const handleFrom = useCallback(
    (from) => patch({ range: { ...range, from } }),
    [patch, range],
  );
  const handleTo = useCallback((to) => patch({ range: { ...range, to } }), [patch, range]);
  const handleFollow = useCallback(
    () => patch({ followDashboardDateFilter: value?.followDashboardDateFilter === false }),
    [patch, value],
  );

  const options = useMemo(
    () => candidates.map((candidate) => ({ id: candidate.ref, name: candidate.label })),
    [candidates],
  );

  return (
    <div className="flex min-w-0 flex-col gap-2 sm:col-span-2">
      <BindingSlotLabel slotKey={slot.key} required={slot.required} />

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <SearchableSelect
          options={options}
          value={value?.ref || null}
          onSelect={handleColumn}
          showAvatar={false}
          disabled={options.length === 0}
          placeholder={t('widgetBuilder.bindingSlots.time.columnPlaceholder')}
          searchPlaceholder={t('widgetBuilder.filters.columnSearchPlaceholder')}
          ariaLabel={t('widgetBuilder.bindingSlots.time.columnAria')}
        />

        {/* The range only means something once there is a column to apply it
            to, and without one the whole binding is left out of the save. */}
        {value?.ref ? (
          <FormSelect
            value={range.mode}
            onChange={handleMode}
            options={modeOptions}
            portalListbox
          />
        ) : null}
      </div>

      {value?.ref ? (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          {grainOptions.length ? (
            <FormSelect
              value={range.grain ?? ''}
              onChange={handleGrain}
              options={grainOptions}
              placeholder={t('widgetBuilder.bindingSlots.time.grainPlaceholder')}
              portalListbox
            />
          ) : null}

          {range.mode === 'LAST_N' ? (
            <NumericField
              value={range.n === '' || range.n === undefined ? null : Number(range.n)}
              onValueChange={handleCount}
              min={1}
              max={366}
              placeholder={t('widgetBuilder.bindingSlots.time.countPlaceholder')}
              aria-label={t('widgetBuilder.bindingSlots.time.countPlaceholder')}
            />
          ) : null}

          {range.mode === 'FIXED' ? (
            <>
              <Textfield
                value={range.from ?? ''}
                onChange={handleFrom}
                placeholder="YYYY-MM-DD"
                aria-label={t('widgetBuilder.bindingSlots.time.from')}
              />
              <Textfield
                value={range.to ?? ''}
                onChange={handleTo}
                placeholder="YYYY-MM-DD"
                aria-label={t('widgetBuilder.bindingSlots.time.to')}
              />
            </>
          ) : null}
        </div>
      ) : null}

      {value?.ref ? (
        <SettingToggle
          checked={value?.followDashboardDateFilter !== false}
          label={t('widgetBuilder.bindingSlots.time.followDashboard')}
          onChange={handleFollow}
        />
      ) : null}

      {options.length === 0 ? (
        <p className="m-0 text-[11px] text-content-muted">
          {t('widgetBuilder.bindingSlots.time.noColumns')}
        </p>
      ) : null}
    </div>
  );
});

TimeBindingRow.propTypes = {
  slot: PropTypes.shape({
    key: PropTypes.string.isRequired,
    required: PropTypes.bool,
  }).isRequired,
  /** `{ ref, range, followDashboardDateFilter }`, or nothing while unset. */
  value: PropTypes.object,
  /** The date-typed items of the pools, each `{ ref, label }`. */
  candidates: PropTypes.arrayOf(PropTypes.object).isRequired,
  onChange: PropTypes.func.isRequired,
};

export default TimeBindingRow;
